import { Button, ButtonGroup, Card, IconButton, LuIcon, Separator, Text } from "@overmap-ai/blocks"
import { type ProcedureStepComment, type Stored } from "@overmap-ai/common"
import { selectUserById } from "@overmap-ai/core"
import { Placeholder } from "@tiptap/extensions"
import { EditorContent, useEditor } from "@tiptap/react"
import { memo, useCallback } from "react"

import { useConfirmDeleteDialog } from "../../hooks/utils"
import { modelIconMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"
import { useAppSelector } from "../../store"
import { BASE_EXTENSIONS, LINK_NODES, MENTION_EXTENSIONS, useEditorContent, useEditorEditable } from "../../tiptap"
import { TextBubbleMenu } from "../../tiptap/components"
import { EditorContext } from "../../tiptap/context"
import { formatCompactDate } from "../../utils/date"
import { getUserDisplayLabel } from "../../utils/users"
import { ProcedureStepCommentDropdownMenu } from "../DropdownMenus/ProcedureStepCommentDropdownMenu"
import { UserIcon } from "../Icons/UserIcon"

interface ProcedureStepCommentActivityTimelineItemProps {
	procedureStepComment: Stored<ProcedureStepComment>
}

export const ProcedureStepCommentActivityTimelineItem = memo<ProcedureStepCommentActivityTimelineItemProps>((props) => {
	const { procedureStepComment } = props

	const createdBy = useAppSelector(selectUserById(procedureStepComment.created_by ?? -1))
	const openConfirmDeleteDialog = useConfirmDeleteDialog()
	const editorContent = useEditorContent(procedureStepComment.content)

	const editor = useEditor(
		{
			editorProps: {
				attributes: {
					class: "outline-none",
				},
			},
			content: editorContent,
			editable: false,
			extensions: [
				...BASE_EXTENSIONS,
				...LINK_NODES,
				...MENTION_EXTENSIONS,
				Placeholder.configure({
					placeholder: "Edit comment",
				}),
			],
		},
		[procedureStepComment.uuid],
	)

	const isEditing = useEditorEditable(editor)

	const handleEditClicked = useCallback(() => {
		editor.setEditable(true, true)
		editor.chain().focus().run()
	}, [editor])

	const handleDelete = useCallback(
		(procedureStepComment: Stored<ProcedureStepComment>) => {
			openConfirmDeleteDialog({
				title: "Delete comment?",
				description: "Are you sure you want to delete this comment? This cannot be undone.",
				onAction: () => sdk.procedureStepComments.delete(procedureStepComment.uuid),
			})
		},
		[openConfirmDeleteDialog],
	)

	const handleResolvedChange = useCallback(
		(procedureStepComment: Stored<ProcedureStepComment>, resolved: boolean) => {
			void sdk.procedureStepComments.update({
				uuid: procedureStepComment.uuid,
				resolved,
			})
		},
		[],
	)

	const handleSubmit = useCallback(() => {
		void sdk.procedureStepComments.update({
			uuid: procedureStepComment.uuid,
			content: JSON.stringify(editor?.getJSON()),
		})
		editor.setEditable(false, true)
		editor.chain().blur().run()
	}, [editor, procedureStepComment.uuid])

	const handleCancel = useCallback(() => {
		editor.commands.setContent(editorContent)
		editor.setEditable(false, true)
		editor.chain().blur().run()
	}, [editor, editorContent])

	const isResolved = procedureStepComment.resolved

	return (
		<div className="flex h-max w-full gap-4">
			<div className="flex shrink-0 flex-col items-center gap-2 overflow-hidden">
				<UserIcon userId={createdBy?.id} size="xs" />
				<Separator orientation="vertical" size="full" className="min-h-0 grow" />
			</div>

			<Card size="xs" className="flex h-max min-w-0 grow flex-col gap-3">
				{!isEditing ? (
					<>
						<div className="flex items-center justify-between gap-2">
							<div className="flex h-max min-w-0 grow items-center gap-2">
								<Text size="xs" className="truncate whitespace-nowrap" accentColor="base">
									<Text highContrast>{getUserDisplayLabel(createdBy)}</Text>
									&nbsp;commented&nbsp;&#8226;&nbsp;
									{formatCompactDate(procedureStepComment.submitted_at)}
								</Text>
							</div>

							<ButtonGroup
								className="flex items-center justify-end gap-2"
								size="xs"
								variant="ghost"
								accentColor="base"
							>
								{isResolved ? (
									<Text size="xs" accentColor="base">
										Resolved
									</Text>
								) : null}
								<ProcedureStepCommentDropdownMenu
									variant="soft"
									accentColor="base"
									size="xs"
									trigger={
										<IconButton>
											<LuIcon icon={modelIconMapping.more} />
										</IconButton>
									}
									procedureStepComment={procedureStepComment}
									onResolvedChange={handleResolvedChange}
									onEdit={handleEditClicked}
									onDelete={handleDelete}
								/>
							</ButtonGroup>
						</div>

						{!isResolved && <Separator size="full" />}
					</>
				) : null}
				{(!isResolved || isEditing) && (
					<EditorContext value={editor}>
						<EditorContent editor={editor} className="min-h-sm h-max text-xs" />
						<TextBubbleMenu editor={editor} />
					</EditorContext>
				)}

				{isEditing && (
					<ButtonGroup className="flex h-max w-full justify-end gap-2" size="xs" variant="soft">
						<Button accentColor="base" onClick={handleCancel}>
							<LuIcon icon={modelIconMapping.close} />
							Cancel
						</Button>
						<Button onClick={handleSubmit} variant="surface">
							<LuIcon icon={modelIconMapping.check} />
							Submit
						</Button>
					</ButtonGroup>
				)}
			</Card>
		</div>
	)
})
ProcedureStepCommentActivityTimelineItem.displayName = "ProcedureStepCommentActivityTimelineItem"
